import { FeatureOptions } from '../../api/models/FeatureOptions';
import { useCallback, useMemo, useRef, useState } from 'preact/hooks';
import ProductAnchorButton from '../../components/product/ProductAnchorButton';
import Popover from '../../components/popover/Popover';
import ActionList, {
    type ActionListItem
} from '../../components/list/ActionList';
import useAppDispatch from '../../hooks/useAppDispatch';
import useAppSelector from '../../hooks/useAppSelector';
import { useTranslation } from '../../hooks/useTranslation';
import { dispatchEvent } from '../../services/EventService';
import type { ClickProductCardActionEvent } from '../../api/events/ClickProductCardActionEvent';

const labels = {
    'toggle-product-comparison-selection': {
        selected: 'PRODUCT_CARD_REMOVE_COMPARISON_BUTTON_LABEL',
        unselected: 'PRODUCT_CARD_COMPARISON_BUTTON_LABEL'
    },
    'product-search-combinations': 'PRODUCT_CARD_COMBINATIONS_BUTTON_LABEL',
    'product-search-similar': 'PRODUCT_CARD_SIMILAR_BUTTON_LABEL',
    'product-detail': 'PRODUCT_CARD_QUESTION_BUTTON_LABEL',
    'product-mix-and-match': 'PRODUCT_CARD_MIX_AND_MATCH_BUTTON_LABEL'
} as const;

const icons = {
    'toggle-product-comparison-selection': 'compare',
    'product-search-combinations': 'join',
    'product-search-similar': 'similar',
    'product-detail': 'question',
    'product-mix-and-match': 'outfit'
} as const;

type ProductContextualMenuFeatureType =
    | 'toggle-product-comparison-selection'
    | Extract<
          FeatureOptions['featureType'],
          | 'product-search-combinations'
          | 'product-mix-and-match'
          | 'product-search-similar'
          | 'product-detail'
      >;

export type ProductContextualMenuProps = {
    featureTypes: ProductContextualMenuFeatureType[];
    productId: string;
    marginTop?: string;
    marginRight?: string;
};

const ProductContextualMenu = ({
    featureTypes,
    productId,
    marginTop,
    marginRight
}: ProductContextualMenuProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const selectedProductIds = useAppSelector(
        state => state.productComparisonState.selectedProductIds
    );
    const [isOpen, setOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);
    const onAnchorClick = useCallback(
        (event: MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            setOpen(!isOpen);
        },
        [isOpen]
    );
    const isSelected = selectedProductIds.includes(productId);

    const getActionListItems = useMemo(
        () =>
            featureTypes.map(featureType => ({
                id: featureType,
                label:
                    featureType === 'toggle-product-comparison-selection'
                        ? t(
                              labels[featureType][
                                  isSelected ? 'selected' : 'unselected'
                              ]
                          )
                        : t(labels[featureType]),
                iconId: icons[featureType]
            })),
        [featureTypes, isSelected]
    );

    const onActionClick = useCallback(
        async (
            _event: MouseEvent,
            item: ActionListItem<ProductContextualMenuFeatureType>
        ) => {
            const featureType = item.id;
            dispatchEvent({
                type: 'bri.click_product_card_action',
                isInteraction: true,
                source: 'brilliant',
                brilliantSource: 'ui',
                action: mapProductContextualMenuFeatureTypeToEventAction(
                    featureType
                ),
                productId
            });
            switch (featureType) {
                case 'toggle-product-comparison-selection':
                    await dispatch.productComparisonState.toggleProductSelection(
                        productId
                    );
                    break;
                case 'product-search-combinations':
                    await dispatch.featureState.startFeature({
                        featureType,
                        baseProductsIds: [productId]
                    });
                    break;
                case 'product-search-similar':
                    await dispatch.featureState.startFeature({
                        featureType,
                        baseProductsIds: [productId]
                    });
                    break;
                case 'product-detail':
                    await dispatch.featureState.startFeature({
                        featureType,
                        productId,
                        source: 'web'
                    });
                    break;
                case 'product-mix-and-match':
                    await dispatch.featureState.startFeature({
                        featureType,
                        productId
                    });
                    break;
            }
            setOpen(false);
        },
        [dispatch]
    );

    return (
        <div className={`bri-ProductContextualMenu`}>
            <ProductAnchorButton
                selected={isSelected || isOpen}
                onClick={onAnchorClick}
                ref={anchorRef}
                marginTop={marginTop}
                marginRight={marginRight}
            />
            <Popover
                anchorRef={anchorRef}
                isOpen={isOpen}
                onClose={() => setOpen(false)}
            >
                <ActionList
                    items={getActionListItems}
                    onClick={onActionClick}
                />
            </Popover>
        </div>
    );
};

export default ProductContextualMenu;

function mapProductContextualMenuFeatureTypeToEventAction(
    featureType: ProductContextualMenuFeatureType
): ClickProductCardActionEvent['action'] {
    switch (featureType) {
        case 'toggle-product-comparison-selection':
            return 'compare';
        case 'product-search-combinations':
            return 'product-search-combinations';
        case 'product-search-similar':
            return 'product-search-similar';
        case 'product-detail':
            return 'product-detail';
        case 'product-mix-and-match':
            return 'product-mix-and-match';
    }
}
